import React, { useState, useEffect, useRef } from 'react';

/**
 * UserMenu.jsx — Benutzer-Menue oben rechts.
 * Klick auf den Namen oeffnet ein Dropdown mit: Glossar/Hilfe, Abmelden.
 * (Dark Mode folgt spaeter als eigenes Thema.)
 */
const S = {
  trigger: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 12, color: 'var(--text-secondary)', background: 'none',
    border: '1px solid var(--border)', borderRadius: 6,
    padding: '5px 10px', cursor: 'pointer',
  },
  menu: {
    position: 'absolute', right: 0, top: 'calc(100% + 6px)',
    background: 'var(--bg-card)', borderRadius: 10, minWidth: 200,
    boxShadow: '0 8px 28px rgba(0,0,0,0.28)', border: '1px solid var(--border)',
    padding: 6, zIndex: 1001,
  },
  item: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    textAlign: 'left', fontSize: 13, color: 'var(--text-primary)',
    background: 'none', border: 'none', borderRadius: 7,
    padding: '9px 10px', cursor: 'pointer',
  },
  sep: { height: 1, background: 'var(--border)', margin: '5px 4px' },
  name: { fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', padding: '6px 10px 2px' },
};

function initialsOf(name) {
  return (name || '').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
}

function Avatar({ name, photoUrl, size = 26 }) {
  const [ok, setOk] = useState(true);
  const base = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };
  if (ok && photoUrl) {
    return <img src={photoUrl} alt="" onError={() => setOk(false)} style={{ ...base, objectFit: 'cover' }} />;
  }
  return (
    <span style={{ ...base, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#185FA5', color: '#fff', fontSize: Math.round(size * 0.42), fontWeight: 600 }}>
      {initialsOf(name)}
    </span>
  );
}

export default function UserMenu({ name, photoUrl, theme, onToggleTheme, onOpenGlossar, onLogout, adminActions }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const hover = (e, on) => { e.currentTarget.style.background = on ? 'var(--bg-hover)' : 'none'; };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button style={S.trigger} onClick={() => setOpen(o => !o)} title="Benutzer-Menü">
        <Avatar name={name} photoUrl={photoUrl} size={24} />
        <span>{name}</span>
        <span style={{ fontSize: 9, color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={S.menu}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 4px' }}>
            <Avatar name={name} photoUrl={photoUrl} size={28} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{name}</span>
          </div>
          {adminActions && adminActions.length > 0 && (
            <>
              <div style={S.sep} />
              {adminActions.map((a) => (
                <div key={a.key}>
                  <button
                    style={{ ...S.item, color: a.running ? 'var(--text-secondary)' : 'var(--text-primary)', opacity: a.running ? 0.6 : 1, paddingBottom: a.status ? 1 : undefined }}
                    onMouseEnter={e => hover(e, true)} onMouseLeave={e => hover(e, false)}
                    // Menue bleibt offen (kein setOpen(false)) — so kann man den
                    // Status darunter live mitverfolgen, statt zu raten ob's fertig ist.
                    onClick={() => { if (!a.running) a.onClick(); }}
                    disabled={a.running}
                    title={a.title}
                  >
                    <span>{a.running ? '⏳' : a.icon}</span>
                    {a.running ? `${a.label} läuft…` : a.label}
                  </button>
                  {a.status && (
                    <div style={{ fontSize: 10.5, color: 'var(--text-tertiary,#94a3b8)', padding: '0 10px 6px 34px' }}>
                      {a.status}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          <div style={S.sep} />
          <button
            style={S.item}
            onMouseEnter={e => hover(e, true)} onMouseLeave={e => hover(e, false)}
            onClick={() => { setOpen(false); onOpenGlossar(); }}
          >
            <span>📖</span> Hilfe
          </button>
          {onToggleTheme && (
            <button
              style={S.item}
              onMouseEnter={e => hover(e, true)} onMouseLeave={e => hover(e, false)}
              onClick={onToggleTheme}
            >
              <span>{theme === 'dark' ? '☀️' : '🌙'}</span>
              {theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
            </button>
          )}
          <div style={S.sep} />
          <button
            style={{ ...S.item, color: '#b91c1c' }}
            onMouseEnter={e => hover(e, true)} onMouseLeave={e => hover(e, false)}
            onClick={onLogout}
          >
            <span>⎋</span> Abmelden
          </button>
        </div>
      )}
    </div>
  );
}
